import { useMemo, useState } from 'react';
import { TriagemEsim } from './TriagemEsim';
import { TriagemBoasVindas } from './TriagemBoasVindas';
import { TriagemPrimeiraFatura } from './TriagemPrimeiraFatura';
import { ArrowUpRight, Clock, Phone, X } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useTriagemFluxo } from './useTriagemFluxo';
import { TriagemFluxoRow } from './TriagemFluxoRow';
import { ManterFilaDialog } from '@/components/kanban/triagem/ManterFilaDialog';
import { JustificativaFilaDialog } from '@/components/kanban/triagem/JustificativaFilaDialog';
import { useLinhasOcupadas } from '@/components/kanban/triagem/acaoTriagem';
import { ehDoSegmento, rotuloSegmento, useSegmentoTriagem } from '@/components/kanban/triagem/segmentoTriagem';
import { TriagemShell, type TriagemShellLegenda } from '@/components/kanban/triagem/TriagemShell';
import { TRIAGEM_TH, TRIAGEM_LARGURA_MINIMA_NOME } from '@/components/kanban/triagem/triagemTabela';
import { calcularTempoTriagem } from '@/lib/tempoComercial';
import type { PedidoTriagemFluxo } from './useTriagemFluxo';
import type { TriagemFluxoConfig } from './triagensFluxoConfig';
import { separarAparelho, useFlagsFaturamento } from '@/components/kanban/triagem/flagsFaturamento';
import { useAuth } from '@/hooks/useAuth';
import { bkoVeSoOsSeus, pedidoEhMeuOuLivre } from '@/lib/fila-permissions';
import { useContratoAdicional } from '@/hooks/useFuncoesAdicionais';
import { SoOsMeusToggle } from '@/components/kanban/triagem/SoOsMeusToggle';
import { useTtFixaInput } from '@/components/kanban/input/TtFixaInput';
import { itensTemFixa } from '@/lib/inputFixaConferencia';
import { InputarFibraDialog, type PortaDaDataFibra } from './InputarFibraDialog';
import { resolverDestinoRetomada } from '@/lib/retomadaDestino';
import type { AcaoFluxoTriagem } from './triagensFluxoConfig';

interface TriagemFluxoModalProps {
  config: TriagemFluxoConfig;
  isOpen: boolean;
  onClose: () => void;
}

export function TriagemFluxoModal({ config, isOpen, onClose }: TriagemFluxoModalProps) {
  if (config.quadro === 'QUALIDADE' && config.coluna === 'ATIVAÇÃO DE CHIP') return <TriagemEsim isOpen={isOpen} onClose={onClose} />;
  if (config.quadro === 'QUALIDADE' && config.coluna === 'BOAS-VINDAS') return <TriagemBoasVindas isOpen={isOpen} onClose={onClose} />;
  if (config.quadro === 'QUALIDADE' && config.coluna === 'PRIMEIRA FATURA') return <TriagemPrimeiraFatura isOpen={isOpen} onClose={onClose} />;
  return <TriagemFluxoPadrao config={config} isOpen={isOpen} onClose={onClose} />;
}

function TriagemFluxoPadrao({ config, isOpen, onClose }: TriagemFluxoModalProps) {
  const {
    pedidos: pedidosTodos,
    isLoading,
    refetch,
    invalidateAllQueries,
    executarAcao,
    manterNoFim,
    isMantendo,
  } = useTriagemFluxo(config);
  /* Só o pedido clicado fica ocupado; o resto da fila continua livre
     (`triagem/acaoTriagem.ts`). */
  const { ocupada, ocupar } = useLinhasOcupadas();

  /* ⛔ CNPJ e CPF NÃO SE MISTURAM NA FILA (Douglas 17/09/2026). A régua de
     entrada desta triagem é a MESMA de sempre — o que muda é a fila mostrar um
     segmento por vez, e os contadores do topo falarem do que está na tela.
     A separação é filtro, nunca coluna nova: nada troca de fila fora da
     triagem. Lei em `triagem/segmentoTriagem.ts`. */
  const { segmento, setSegmento } = useSegmentoTriagem();
  /* Nas filas do contrato (Input, Pendência Input, Aguardando HP) cada BKO vê
     só os pedidos dele e os sem dono: o topo da fila é sempre o mais antigo
     DELE, e ninguém adia o pedido do colega para chegar no seu (Douglas
     09/10/2026, régua em `lib/fila-permissions.ts`). Supervisora e gestão
     veem tudo. */
  const { profile } = useAuth();
  const { adicional } = useContratoAdicional();
  const [soOsMeusAdicional, setSoOsMeusAdicional] = useState(true);
  const comToggle = !!config.porDono && adicional;
  const soOsSeus = !!config.porDono
    && (bkoVeSoOsSeus(profile as { funcao?: string | null } | null) || (adicional && soOsMeusAdicional));
  const pedidos = useMemo(
    () => pedidosTodos.filter((r) => ehDoSegmento(r.tipo_pessoa, segmento)
      && (!soOsSeus || pedidoEhMeuOuLivre(r.dono_id, profile?.id))),
    [pedidosTodos, segmento, soOsSeus, profile?.id],
  );

  const ttDaLinha = useTtFixaInput(pedidos, isOpen && config.coluna === 'INPUT');

  const [pedidoManter, setPedidoManter] = useState<PedidoTriagemFluxo | null>(null);
  /* O input da fixa pede a data e o turno da instalação no próprio clique
     (Douglas 30/10/2026): quem inputa marca a data oficial. */
  const [inputarFibra, setInputarFibra] = useState<{ pedido: PedidoTriagemFluxo; acao: AcaoFluxoTriagem; porta: PortaDaDataFibra } | null>(null);
  const executarDaLinha = (p: PedidoTriagemFluxo, acao: AcaoFluxoTriagem) => {
    void ocupar(p.pedido_id, async () => {
      const inputar = acao.key === 'input-realizado' || acao.key === 'inputar';
      if (inputar || acao.tipo === 'retomar') {
        let fixa: boolean;
        try {
          /* A fibra que volta para a Em instalação pede a data e o turno novos
             que a Claro marcou (maquete v7, Douglas 30/10/2026). */
          const itens = inputar
            ? p.itens.map((i) => i.item_id)
            : (await Promise.all(p.itens.map(async (i) => ({ id: i.item_id, destino: await resolverDestinoRetomada(i.item_id) }))))
              .filter((d) => d.destino.coluna === 'EM INSTALAÇÃO').map((d) => d.id);
          fixa = await itensTemFixa(itens);
        } catch (e) {
          toast({ title: inputar ? 'Não deu para abrir o Inputar' : 'Não deu para abrir o Retomar', description: e instanceof Error ? e.message : 'Tente novamente.', variant: 'destructive' });
          return;
        }
        if (fixa) {
          setInputarFibra({ pedido: p, acao, porta: inputar ? 'inputar' : 'retomar' });
          return;
        }
      }
      await executarAcao({ pedido: p, acao });
    }).catch(() => undefined);
  };

  const totalItens = pedidos.reduce((sum, p) => sum + p.itens.length, 0);
  // Aparelho anda SEPARADO do faturamento — tem KPI próprio e não é venda de
  // linha. Some do R$ da fila, não some da tela. Régua em `flagsFaturamento.ts`.
  // Soma pelos ITENS (e não por `p.valor_total`), que é onde mora o produto.
  const flagsFat = useFlagsFaturamento();
  const { faturamento: totalValue, qtdAparelhos } = separarAparelho(
    pedidos.flatMap((p) => p.itens), flagsFat, (i) => i.produto, (i) => i.subtotal || 0,
  );

  /* SLA e "mais antigo" saem do MESMO cálculo que pinta o chip de cada linha
     (`calcularTempoTriagem`) — o cabeçalho não pode contar de um jeito e a
     linha de outro. */
  const { estourados, maisAntigo } = useMemo(() => {
    let fora = 0;
    let topo: { label: string; estourado: boolean } | null = null;
    let maiorHoras = -1;

    for (const p of pedidos) {
      const t = calcularTempoTriagem(p.entrada_coluna_em, config.prazoHoras);
      if (t.status === 'atrasado') fora += 1;
      if (t.horasUteis > maiorHoras) {
        maiorHoras = t.horasUteis;
        topo = { label: t.label, estourado: t.status === 'atrasado' };
      }
    }
    return { estourados: fora, maisAntigo: topo };
  }, [pedidos, config.prazoHoras]);

  /* Legenda dos ícones da LINHA — as cores por ação do config (indigo/âmbar/
     verde) deixaram de pintar aqui; os campos continuam no config porque os
     ÍCONES da linha ainda os usam. */
  const legenda = useMemo<TriagemShellLegenda[]>(() => {
    const itens: TriagemShellLegenda[] = [
      { key: 'analisar', Icone: ArrowUpRight, label: 'Analisar' },
    ];
    if (config.mostrarCobrar) itens.push({ key: 'cobrar', Icone: Phone, label: 'Cobrar' });
    for (const acao of config.acoes) {
      itens.push({ key: acao.key, Icone: acao.Icone, label: acao.legenda });
    }
    if (config.permitirManterFimFila) {
      itens.push({ key: 'fim-fila', Icone: Clock, label: config.rotuloManter ?? 'Fim da fila' });
    }
    itens.push({ key: 'cancelar', Icone: X, label: 'Cancelar' });
    return itens;
  }, [config]);

  /* Soma das colunas fixas abaixo; a Razão Social nunca fica com menos que o
     mínimo do nome (`triagemTabela.ts`). */
  const larguraFixa = 46 + 122 + 158 + 92 + 78 + 34 + 84 + 128 + 200
    + (config.mostrarCobrar ? 110 : 0) + (config.mostrarDestinoRetomada ? 120 : 0);

  return (
    <TriagemShell
      isOpen={isOpen}
      onFechar={onClose}
      Icone={config.Icone}
      titulo={config.titulo}
      contadores={{ pedidos: pedidos.length, itens: totalItens, valor: totalValue, aparelhos: qtdAparelhos }}
      segmento={segmento}
      onSegmento={setSegmento}
      cabecalhoExtra={comToggle ? <SoOsMeusToggle soOsMeus={soOsMeusAdicional} onChange={setSoOsMeusAdicional} /> : undefined}
      sla={config.prazoHoras ? { horas: config.prazoHoras, estourados } : null}
      fifo={config.fifo}
      legenda={legenda}
      isLoading={isLoading}
      onAtualizar={async () => {
        await refetch();
        toast({ title: 'Dados atualizados', duration: 1500 });
      }}
      vazio={
        pedidos.length === 0
          ? soOsSeus
            ? { titulo: 'Nenhum pedido seu nesta fila', descricao: 'Os pedidos dos colegas ficam com eles.' }
            : {
              titulo: 'Nenhum pedido pendente',
              descricao: 'Todos os pedidos desta fila foram processados',
            }
          : undefined
      }
      maisAntigo={maisAntigo}
    >
      <table className="w-full" style={{ tableLayout: 'fixed', minWidth: larguraFixa + TRIAGEM_LARGURA_MINIMA_NOME }}>
        <thead className="sticky top-0 z-10 bg-surface">
          <tr>
            <th className={`${TRIAGEM_TH} w-[46px]`}>Fila</th>
            <th className={`${TRIAGEM_TH} w-[122px]`}>{rotuloSegmento(segmento)}</th>
            <th className={TRIAGEM_TH}>Razão Social</th>
            <th className={`${TRIAGEM_TH} w-[158px]`}>Consultor</th>
            <th className={`${TRIAGEM_TH} w-[92px]`}>Pedido</th>
            <th className={`${TRIAGEM_TH} w-[78px]`}>Produto</th>
            <th className={`${TRIAGEM_TH} w-[34px]`}>Qtd</th>
            <th className={`${TRIAGEM_TH} w-[84px]`}>Valor</th>
            <th className={`${TRIAGEM_TH} w-[128px]`}>Tempo</th>
            {config.mostrarCobrar && <th className={`${TRIAGEM_TH} w-[110px]`}>Contato</th>}
            {config.mostrarDestinoRetomada && (
              <th className={`${TRIAGEM_TH} w-[120px]`}>Volta p/</th>
            )}
            <th className={`${TRIAGEM_TH} w-[200px]`}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {pedidos.map((pedido, index) => (
            <TriagemFluxoRow
              key={pedido.pedido_id}
              config={config}
              pedido={pedido}
              posicao={index}
              isTopo={index === 0}
              isProcessing={ocupada(pedido.pedido_id)}
              onAcao={executarDaLinha}
              onManterFimFila={setPedidoManter}
              onAfterCancel={invalidateAllQueries}
              tt={ttDaLinha(pedido)}
            />
          ))}
        </tbody>
      </table>

      <InputarFibraDialog
        pedido={inputarFibra?.pedido ?? null}
        porta={inputarFibra?.porta}
        isLoading={!!inputarFibra && ocupada(inputarFibra.pedido.pedido_id)}
        onOpenChange={(open) => {
          if (!open) setInputarFibra(null);
        }}
        onConfirm={(agenda) => {
          if (!inputarFibra) return;
          const { pedido: p, acao } = inputarFibra;
          void ocupar(p.pedido_id, () => executarAcao({ pedido: p, acao, agenda }))
            .then(() => setInputarFibra(null))
            .catch(() => undefined);
        }}
      />

      {config.justificativaDaFila ? <JustificativaFilaDialog
        open={!!pedidoManter}
        onOpenChange={(open) => {
          if (!open) setPedidoManter(null);
        }}
        coluna={config.coluna}
        numeroPedido={pedidoManter?.numero_pedido || ''}
        razaoSocial={pedidoManter?.razao_social || ''}
        diasParado={pedidoManter?.entrada_coluna_em
          ? Math.floor((Date.now() - new Date(pedidoManter.entrada_coluna_em).getTime()) / 86400000) : null}
        isLoading={isMantendo}
        onConfirm={async (justificativa, codigos) => {
          if (!pedidoManter?.itens[0]) return;
          await manterNoFim({ pedido: pedidoManter, justificativa, justificativaDaFila: true,
            motivoCodigo: codigos.motivo, acaoCodigo: codigos.acao });
          setPedidoManter(null);
        }}
      /> : <ManterFilaDialog
        open={!!pedidoManter}
        onOpenChange={(open) => {
          if (!open) setPedidoManter(null);
        }}
        numeroPedido={pedidoManter?.numero_pedido || ''}
        razaoSocial={pedidoManter?.razao_social || ''}
        isLoading={isMantendo}
        onConfirm={async ({ justificativa, prd }) => {
          if (!pedidoManter) return;
          try {
            await manterNoFim({ pedido: pedidoManter, justificativa, prd });
            setPedidoManter(null);
          } catch {
            /* toast no hook */
          }
        }}
      />}
    </TriagemShell>
  );
}
